// src/utils/analyticsUtils.js
// Utilities for formatting and transforming analytics data

/**
 * Formats a number for display
 * @param {number} value - Number to format
 * @param {string} format - Format type: 'compact', 'decimal', 'percent', 'currency'
 * @param {Object} options - Additional formatting options 
 * @returns {string} Formatted number
 */
export const formatNumber = (value, format = 'compact', options = {}) => {
  if (value === null || value === undefined) return 'N/A';
  
  try {
    switch (format) {
      case 'compact':
        return new Intl.NumberFormat('en', {
          notation: 'compact',
          maximumFractionDigits: 1,
          ...options
        }).format(value);
        
      case 'decimal':
        return new Intl.NumberFormat('en', {
          maximumFractionDigits: options.digits || 2,
          minimumFractionDigits: options.minDigits || 0,
          ...options
        }).format(value);
        
      case 'percent':
        return new Intl.NumberFormat('en', {
          style: 'percent',
          maximumFractionDigits: 1,
          ...options
        }).format(value / 100);
        
      case 'currency':
        return new Intl.NumberFormat('en', {
          style: 'currency',
          currency: options.currency || 'USD',
          ...options
        }).format(value);
        
      default:
        return value.toString();
    }
  } catch (error) {
    console.error('Error formatting number:', error);
    return value.toString();
  }
};

/**
 * Calculates percent change between two values
 * @param {number} current - Current value
 * @param {number} previous - Previous value
 * @param {number} precision - Number of decimal places
 * @returns {number} Percent change
 */
export const calculatePercentChange = (current, previous, precision = 1) => {
  if (!current || !previous) return 0;
  
  const change = ((current - previous) / previous) * 100;
  
  // Round to specified precision
  return Math.round(change * (10 ** precision)) / (10 ** precision);
};

/**
 * Transforms time series data for charts
 * @param {Array} data - Raw time series data
 * @param {Object} options - Configuration options
 * @returns {Array} Formatted data for charting
 */
export const transformTimeSeriesData = (data, options = {}) => {
  if (!data || !Array.isArray(data)) return [];
  
  const {
    dateKey = 'date',
    dateFormat = 'short', // 'short', 'long', 'numeric'
    metrics = [], // array of metric keys to include
    sortData = true,
  } = options;

  // Format date appropriately
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      
      switch (dateFormat) {
        case 'short':
          return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
        case 'long':
          return date.toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' });
        case 'numeric':
          return date.toLocaleDateString('en');
        default:
          return dateStr;
      }
    } catch (error) {
      return dateStr;
    }
  };

  // Transform the data
  const transformed = data.map(item => {
    const result = { 
      [dateKey]: formatDate(item[dateKey])
    };
    
    // Add all requested metrics
    metrics.forEach(metric => {
      if (item[metric] !== undefined) {
        result[metric] = item[metric];
      } else {
        result[metric] = 0;
      }
    });
    
    return result;
  });
  
  // Sort by date if requested
  if (sortData) {
    transformed.sort((a, b) => new Date(a[dateKey]) - new Date(b[dateKey]));
  }
  
  return transformed;
};

/**
 * Transforms distribution data for pie/donut charts
 * @param {Array|Object} data - Raw distribution data
 * @param {Object} options - Configuration options
 * @returns {Array} Formatted data for pie charts
 */
export const transformDistributionData = (data, options = {}) => {
  if (!data) return [];
  
  const {
    nameKey = 'name',    // key to use for segment names
    valueKey = 'value',  // key to use for segment values
    labelKey,            // optional custom label key 
    colorKey,            // optional color key
    colors,              // optional array of colors to use
    sortBy = 'value',    // 'value', 'name', or null for no sorting
    sortDirection = 'desc',
    threshold = 0,       // minimum value to include (smaller values combined into "Other")
    calculatePercent = false,
  } = options;
  
  let formattedData = [];
  
  // Handle object input (convert to array)
  if (!Array.isArray(data) && typeof data === 'object') {
    formattedData = Object.entries(data).map(([key, value]) => ({
      [nameKey]: key,
      [valueKey]: typeof value === 'object' ? value[valueKey] || 0 : value,
      ...(typeof value === 'object' ? value : {})
    }));
  } else if (Array.isArray(data)) {
    formattedData = [...data];
  } else {
    return [];
  }
  
  // Handle threshold (combine small values into "Other")
  if (threshold > 0) {
    let otherValue = 0;
    const filteredData = [];
    
    formattedData.forEach(item => {
      if (item[valueKey] >= threshold) {
        filteredData.push(item);
      } else {
        otherValue += item[valueKey];
      }
    });
    
    if (otherValue > 0) {
      filteredData.push({
        [nameKey]: 'Other',
        [valueKey]: otherValue
      });
    }
    
    formattedData = filteredData;
  }
  
  // Sort data if requested
  if (sortBy) {
    formattedData.sort((a, b) => {
      if (sortDirection === 'desc') {
        return b[sortBy] - a[sortBy];
      }
      return a[sortBy] - b[sortBy];
    });
  }
  
  // Calculate percentages if requested
  if (calculatePercent) {
    const total = formattedData.reduce((sum, item) => sum + item[valueKey], 0);
    
    formattedData = formattedData.map(item => ({
      ...item,
      percent: total > 0 ? (item[valueKey] / total) * 100 : 0
    }));
  }
  
  // Add custom labels if requested
  if (labelKey) {
    formattedData = formattedData.map(item => ({
      ...item,
      [labelKey]: item[labelKey] || `${item[nameKey]}: ${item[valueKey]}`
    }));
  }
  
  // Add colors if provided
  if (colors && Array.isArray(colors)) {
    formattedData = formattedData.map((item, index) => ({
      ...item,
      [colorKey || 'color']: item[colorKey] || colors[index % colors.length]
    }));
  }
  
  return formattedData;
};

/**
 * Creates comparison data for different time periods
 * @param {Array} currentData - Current period data
 * @param {Array} previousData - Previous period data
 * @param {Object} options - Configuration options
 * @returns {Array} Comparison data
 */
export const createComparisonData = (currentData, previousData, options = {}) => {
  if (!currentData || !previousData) return [];
  
  const {
    metrics = [],      // Metrics to compare
    idKey = 'id',      // Key to match items between periods
    nameKey = 'name',  // Display name for the item
    calculateChange = true
  } = options;
  
  // Create a map of previous period data
  const previousMap = new Map();
  previousData.forEach(item => {
    previousMap.set(item[idKey], item);
  });
  
  // Compare each item in current data with previous
  return currentData.map(current => {
    const previous = previousMap.get(current[idKey]);
    const result = {
      [idKey]: current[idKey],
      [nameKey]: current[nameKey] || current[idKey]
    };
    
    metrics.forEach(metric => {
      // Current value
      result[`current_${metric}`] = current[metric] || 0;
      
      // Previous value 
      result[`previous_${metric}`] = previous ? (previous[metric] || 0) : 0;
      
      // Calculate change if requested
      if (calculateChange) {
        const currentVal = current[metric] || 0;
        const previousVal = previous ? (previous[metric] || 0) : 0;
        
        if (previousVal !== 0) {
          result[`change_${metric}`] = calculatePercentChange(currentVal, previousVal);
        } else {
          result[`change_${metric}`] = currentVal > 0 ? 100 : 0; // If previous was 0, any increase is 100%
        }
      }
    });
    
    return result;
  });
};

/**
 * Generates an analysis of the given analytics data
 * @param {Object} data - Analytics data to analyze
 * @param {Object} options - Analysis options
 * @returns {Object} Analysis results and insights
 */
export const generateAnalyticsInsights = (data, options = {}) => {
  if (!data) return { insights: [] };
  
  const {
    metrics = [], // metrics to analyze
    comparisonThreshold = 5, // percent change to consider significant
    maxInsights = 5  // maximum number of insights to generate
  } = options;
  
  const insights = [];
  
  // Check for significant changes
  metrics.forEach(metric => {
    if (data[metric] && data[`previous${metric.charAt(0).toUpperCase() + metric.slice(1)}`]) {
      const current = data[metric];
      const previous = data[`previous${metric.charAt(0).toUpperCase() + metric.slice(1)}`];
      const percentChange = calculatePercentChange(current, previous);
      
      if (Math.abs(percentChange) >= comparisonThreshold) {
        insights.push({
          metric,
          title: `${metric.charAt(0).toUpperCase() + metric.slice(1)} ${percentChange >= 0 ? 'increased' : 'decreased'} by ${Math.abs(percentChange)}%`,
          description: `${metric.charAt(0).toUpperCase() + metric.slice(1)} ${percentChange >= 0 ? 'increased' : 'decreased'} from ${previous} to ${current} (${percentChange >= 0 ? '+' : ''}${percentChange}%)`,
          impact: percentChange >= 0 ? 'positive' : 'negative',
          percentChange
        });
      }
    }
  });
  
  // Sort insights by absolute percent change
  insights.sort((a, b) => Math.abs(b.percentChange) - Math.abs(a.percentChange));
  
  // Limit to max insights
  const topInsights = insights.slice(0, maxInsights);
  
  return {
    insights: topInsights,
    hasTrends: topInsights.length > 0,
    topMetric: topInsights.length > 0 ? topInsights[0].metric : null,
    topChange: topInsights.length > 0 ? topInsights[0].percentChange : 0
  };
};

/**
 * Aggregates data by a specified dimension
 * @param {Array} data - Data array to aggregate
 * @param {string} dimension - Dimension to aggregate by
 * @param {Array} metrics - Metrics to aggregate
 * @param {Function} aggregator - Aggregation function (default: sum)
 * @returns {Array} Aggregated data
 */
export const aggregateDataBy = (data, dimension, metrics, aggregator = (values) => values.reduce((sum, val) => sum + val, 0)) => {
  if (!data || !Array.isArray(data) || !dimension || !metrics || !Array.isArray(metrics)) {
    return [];
  }
  
  const aggregated = {};
  
  data.forEach(item => {
    const key = item[dimension];
    if (!key) return;
    
    if (!aggregated[key]) {
      aggregated[key] = {
        [dimension]: key,
        count: 0
      };
      
      metrics.forEach(metric => {
        aggregated[key][metric] = 0;
      });
    }
    
    aggregated[key].count += 1;
    
    metrics.forEach(metric => {
      if (item[metric] !== undefined) {
        aggregated[key][metric] += item[metric];
      }
    });
  });
  
  return Object.values(aggregated);
};